import environment from "environment";
import React from "react";
import { Pagination } from "react-bootstrap";
import BaseSelect from "../base-form/BaseSelect";

interface IProps {
    readonly pageSize?: number;
    readonly visible?: boolean;
    readonly total: number;
    readonly paginationUpdate: (firstElem: number, lastElem: number) => void;
    readonly className?: string;
    readonly showElementi?: boolean;
}
interface IState {
    elemPerPage: number;
    firstPage: number;
    lastPage: number;
    pageNumbers: number[];
    currPage: number;
    firstElem: number;
    lastElem: number;
}

class BasePagination extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            elemPerPage: props.pageSize ?? environment.numeroElementiPerPaginaTabella,
            firstPage: 0,
            lastPage: 0,
            pageNumbers: [] as number[],
            currPage: 0,
            firstElem: 0,
            lastElem: 0,
        } as IState;
    }

    public componentDidMount(): void {
        this.initPagination();
    }

    public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
        if (prevProps.total !== this.props.total) {
            this.initPagination();
        }
    }

    private hasUnmounted = false;

    public componentWillUnmount() {
        this.hasUnmounted = true;
    }

    private getElemPerPage = () => {
        const { visible, total } = this.props;
        const { elemPerPage } = this.state;
        if (visible) {
            return elemPerPage;
        } else {
            return total;
        }
    };

    private initPagination() {
        const elemPerPage = this.getElemPerPage();
        const firstPage = 1;
        const lastPage = Math.ceil(this.props.total / elemPerPage) || 1;
        let pageNumbers: number[] = [];
        for (let i: number = firstPage; i <= lastPage; i++) {
            pageNumbers.push(i);
        }
        if (this.hasUnmounted === false) {
            this.setState(
                {
                    firstPage: firstPage,
                    lastPage: lastPage,
                    pageNumbers: pageNumbers,
                    currPage: 0,
                    firstElem: 0,
                    lastElem: 0,
                },
                () => this.setPage(1)
            );
        }
    }

    private setPage = (pageNumber: number) => {
        const elemPerPage = this.getElemPerPage();
        if (pageNumber < 1) {
            pageNumber = 1;
        }
        if (pageNumber > this.state.lastPage) {
            pageNumber = this.state.lastPage;
        }
        let lastElem = elemPerPage * pageNumber;
        let firstElem = lastElem - elemPerPage;
        if (this.hasUnmounted === false) {
            this.setState(
                {
                    currPage: pageNumber,
                    firstElem: firstElem,
                    lastElem: lastElem,
                },
                () => {
                    this.props.paginationUpdate(firstElem, lastElem);
                }
            );
        }
    };

    private setElemPerPage = (elemPerPage: number) => {
        if (this.hasUnmounted === false) {
            this.setState(
                {
                    elemPerPage: elemPerPage,
                },
                () => {
                    this.initPagination();
                }
            );
        }
    };

    private getNavigablePages = () => {
        const { currPage, firstPage, lastPage, pageNumbers } = this.state;
        const pagesLimit = 5;
        if (pageNumbers.length > pagesLimit) {
            let result: number[] = [];
            const diff = pagesLimit - 2;
            const firstLimit = firstPage + diff;
            const lastLimit = lastPage - diff;
            result.push(firstPage);
            if (currPage < firstLimit) {
                for (let i = firstPage + 1; i < firstLimit; i++) {
                    result.push(i);
                }
                result.push(0);
            }
            if (currPage >= firstLimit && currPage <= lastLimit) {
                result.push(0);
                result.push(currPage);
                result.push(0);
            }
            if (currPage > lastLimit) {
                result.push(0);
                for (let i = lastLimit + 1; i < lastPage; i++) {
                    result.push(i);
                }
            }
            result.push(lastPage);
            return result;
        } else {
            return pageNumbers;
        }
    };

    private getElemPerPageOptions = () => {
        const { total } = this.props;
        let result: number[] = [];
        result.push(1);
        result.push(5);
        result.push(10);
        if (total > 10) {
            result.push(25);
        }
        if (total > 25) {
            result.push(50);
        }
        if (total > 50) {
            result.push(75);
        }
        if (total > 75) {
            result.push(100);
        }
        return result;
    };

    public render() {
        const { total, visible, className, showElementi } = this.props;

        const { currPage, firstPage, lastPage, elemPerPage } = this.state;

        return (
            <>
                {total > 0 && visible !== false && (
                    <div className={["ml-auto align-self-center", className].join(" ")}>
                        <Pagination className="justify-content-end mb-0 rounded-0">
                            <Pagination.Prev
                                onClick={() => this.setPage(currPage - 1)}
                                disabled={currPage === firstPage}
                            />
                            {this.getNavigablePages().map((pageNumber: number, pageIndex: number) => (
                                <Pagination.Item
                                    key={pageIndex}
                                    active={pageNumber === currPage}
                                    onClick={pageNumber > 0 ? () => this.setPage(pageNumber) : undefined}
                                    disabled={pageNumber === 0}
                                >
                                    {pageNumber || "..."}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                onClick={() => this.setPage(currPage + 1)}
                                disabled={currPage === lastPage}
                            />
                            {showElementi && (
                                <div className="d-flex ml-2 pl-2 border-left">
                                    <BaseSelect
                                        controlId={"pagination-page-size"}
                                        name={"pagination-page-size"}
                                        label={"Elementi"}
                                        hoverTitle={"Numero elementi per pagina"}
                                        value={elemPerPage.toString()}
                                        onChangeHandler={(name, value) => this.setElemPerPage(value)}
                                        required={false}
                                        options={this.getElemPerPageOptions().map(
                                            (elemPerPageOption: number, elemPerPageOptionIndex: number) => (
                                                <option key={elemPerPageOptionIndex} value={elemPerPageOption}>
                                                    {elemPerPageOption}
                                                </option>
                                            )
                                        )}
                                        isInvalid={false}
                                        optionEmpty={false}
                                    />
                                </div>
                            )}
                        </Pagination>
                    </div>
                )}
            </>
        );
    }
}

export { BasePagination as Unconnected };
export default BasePagination;
